import {QueryContractInfoResponse} from "@shareledgerjs/types/cosmwasm/wasm/v1/query";

import {BaseClient} from "../../baseclient";
import {WasmQueryExtension} from "../wasm";
import {
  BalanceResponse,
  BatchBalanceResponse,
  ContractInfoResponse,
  QueryMsg as Cw1155BaseQueryMsg,
  QueryMsg as Cw1155RoyaltiesQueryMsg,
  TokenInfoResponse
} from "./types/Cw1155Royalties.types";
import {
  // AllNftInfoResponseForEmpty,
  AllNftInfoResponseForCw2981QueryMsg,
  Approval,
  MinterResponse,
  NumTokensResponse,
  OperatorsResponse,
  OwnerOfResponse,
  QueryMsg as Cw2981RoyaltiesAutoincrementQueryMsg,
  TokensResponse
} from "./types/Cw2981RoyaltiesAutoincrement.types";

export interface NftQueryExtensionMethods {
  balance(contractAddress: string, owner: string, tokenId: string): Promise<string>;
  balances(contractAddress: string, owner: string, tokenIds: string[]): Promise<Record<string, string>>;
  ownerOf(contractAddress: string, tokenId: string, includeExpired?: boolean): Promise<OwnerOfResponse>;
  minter(contractAddress: string): Promise<string>;
  nftInfo(contractAddress: string, tokenId: string, includeExpired?: boolean): Promise<any>;
  operators(contractAddress: string, owner: string, includeExpired?: boolean, after?: string, limit?: number): Promise<Approval[]>;
  tokens(contractAddress: string, address?: string, after?: string, limit?: number): Promise<string[]>;
  tokensCount(contractAddress: string): Promise<number>;
  tokenMetadata(contractAddress: string, tokenId: string): Promise<TokenInfoResponse>;
  contractMetadata(contractAddress: string): Promise<ContractInfoResponse>;
  metadata(contractAddress: string): Promise<QueryContractInfoResponse>;
}

export interface NftQueryExtension {
  readonly nft: NftQueryExtensionMethods;
}

export function NftQueryExtension<T extends {new (...args: any[]): BaseClient & NftQueryExtension & WasmQueryExtension}>(
  constructor: T
): T {
  return class extends constructor {
    override get nft() {
      return {
        ...super["nft"],
        balance: async (contractAddress: string, owner: string, tokenId: string) => {
          const res = await this.wasm.smartContractState<BalanceResponse, Cw1155BaseQueryMsg>(contractAddress, {
            balance: {
              token_id: tokenId,
              owner
            }
          });
          return res.balance;
        },
        balances: async (contractAddress: string, owner: string, tokenIds: string[]) => {
          const res = await this.wasm.smartContractState<BatchBalanceResponse, Cw1155BaseQueryMsg>(contractAddress, {
            batch_balance: {
              owner,
              token_ids: tokenIds
            }
          });
          return tokenIds.reduce<Record<string, string>>((prev, curr, idx) => {
            prev[curr] = res.balances[idx];
            return prev;
          }, {});
        },
        ownerOf: async (contractAddress: string, tokenId: string, includeExpired = false) => {
          const res = await this.wasm.smartContractState<OwnerOfResponse, Cw2981RoyaltiesAutoincrementQueryMsg>(contractAddress, {
            owner_of: {
              token_id: tokenId,
              include_expired: includeExpired
            }
          });
          return res;
        },
        minter: async (contractAddress: string) => {
          const res = await this.wasm.smartContractState<MinterResponse, Cw2981RoyaltiesAutoincrementQueryMsg>(contractAddress, {
            minter: {}
          });
          return res.minter;
        },
        nftInfo: async (contractAddress: string, tokenId: string, includeExpired = false) => {
          const res = await this.wasm.smartContractState<AllNftInfoResponseForCw2981QueryMsg, Cw2981RoyaltiesAutoincrementQueryMsg>(
            contractAddress,
            {
              all_nft_info: {
                token_id: tokenId,
                include_expired: includeExpired
              }
            }
          );
          return res;
        },
        operators: async (contractAddress: string, owner: string, includeExpired?: boolean, after?: string, limit = 10) => {
          const res = await this.wasm.smartContractState<OperatorsResponse, Cw2981RoyaltiesAutoincrementQueryMsg>(contractAddress, {
            all_operators: {
              owner,
              include_expired: includeExpired,
              limit,
              start_after: after
            }
          });
          return res.operators;
        },
        tokens: async (contractAddress: string, address?: string, after?: string, limit = 10) => {
          const res = await this.wasm.smartContractState<TokensResponse, Cw2981RoyaltiesAutoincrementQueryMsg>(
            contractAddress,
            address
              ? {
                  tokens: {
                    owner: address,
                    limit,
                    start_after: after
                  }
                }
              : {
                  all_tokens: {
                    limit,
                    start_after: after
                  }
                }
          );
          return res.tokens;
        },
        tokensCount: async (contractAddress: string) => {
          const res = await this.wasm.smartContractState<NumTokensResponse, Cw2981RoyaltiesAutoincrementQueryMsg>(contractAddress, {
            num_tokens: {}
          });
          return res.count;
        },
        tokenMetadata: async (contractAddress: string, tokenId: string) => {
          const res = await this.wasm.smartContractState<TokenInfoResponse, Cw1155BaseQueryMsg>(contractAddress, {
            token_info: {
              token_id: tokenId
            }
          });
          return res;
        },
        contractMetadata: async (contractAddress: string) => {
          const res = await this.wasm.smartContractState<ContractInfoResponse, Cw1155RoyaltiesQueryMsg>(contractAddress, {
            contract_info: {}
          });
          return res;
        },
        metadata: async (contractAddress: string) => {
          const res = await this.wasm.contractInfo(contractAddress);
          return res;
        }
      };
    }
  };
}
